// Viewport math shared by the zoom-to-fit saga and the diagram search.
// World->screen mapping: screen = world * scale + origin (see Canvas.jsx).

// Screen chrome offsets — extracted from onUpdateScaleToFit (diagram.sagas.js).
export const FIT_OFFSET = 65;
export const FIT_LEFT_EXTRA = 25;
export const FIT_OFFSET_TOP = 75;
export const FIT_OFFSET_BOTTOM = 70;

const ELEMENT_MARGIN_RATIO = 0.2; // element bbox + 20% margin per side
const ELEMENT_MAX_ZOOM = 1.0;     // never zoom past 100% onto a single element
const MIN_SCALE = 0.045;          // reducer clamp lower bound

// Fallback for elements whose size is not yet measured
const FALLBACK_WIDTH = 220;
const FALLBACK_HEIGHT = 120;

function usableCanvas(windowW, windowH) {
    return {
        width: windowW - 2 * FIT_OFFSET,
        height: windowH - (FIT_OFFSET_TOP + FIT_OFFSET_BOTTOM),
    };
}

// Origin that centers bbox on screen at `scale`.
function centerOrigin(bbox, scale, windowW, windowH) {
    const canvas = usableCanvas(windowW, windowH);
    return {
        x: (canvas.width - bbox.width * scale) / 2 - bbox.x * scale + FIT_OFFSET + FIT_LEFT_EXTRA,
        y: (canvas.height - bbox.height * scale) / 2 - bbox.y * scale + FIT_OFFSET_TOP,
    };
}

// Whole-content zoom-to-fit (the math onUpdateScaleToFit used inline).
export function computeFitViewport(bbox, { windowW, windowH, maxZoom }) {
    const canvas = usableCanvas(windowW, windowH);
    const scale = Math.min(canvas.width / bbox.width, canvas.height / bbox.height, maxZoom);
    return { scale, ...centerOrigin(bbox, scale, windowW, windowH) };
}

// Single-element viewport for search preview/commit: keep the current zoom
// when bbox + margin fits on screen (pan only), otherwise zoom so it fits,
// capped at 100% and floored at the canvas minimum scale.
export function computeElementViewport(bbox, currentScale, windowW, windowH) {
    const safe = {
        x: bbox.x ?? 0,
        y: bbox.y ?? 0,
        width: bbox.width > 0 ? bbox.width : FALLBACK_WIDTH,
        height: bbox.height > 0 ? bbox.height : FALLBACK_HEIGHT,
    };
    const padded = {
        x: safe.x - safe.width * ELEMENT_MARGIN_RATIO,
        y: safe.y - safe.height * ELEMENT_MARGIN_RATIO,
        width: safe.width * (1 + 2 * ELEMENT_MARGIN_RATIO),
        height: safe.height * (1 + 2 * ELEMENT_MARGIN_RATIO),
    };

    const canvas = usableCanvas(windowW, windowH);
    const fitsAtCurrent =
        padded.width * currentScale <= canvas.width &&
        padded.height * currentScale <= canvas.height;

    if (fitsAtCurrent) {
        return { scale: currentScale, ...centerOrigin(safe, currentScale, windowW, windowH) };
    }

    const fit = computeFitViewport(padded, { windowW, windowH, maxZoom: ELEMENT_MAX_ZOOM });
    const scale = Math.max(fit.scale, MIN_SCALE);
    return { scale, ...centerOrigin(safe, scale, windowW, windowH) };
}
